import React, { useState } from "react";
import {
  EyeIcon,
  EyeOffIcon,
  UserIcon,
  LockIcon,
  Loader2Icon,
  Dna,
  AlertCircleIcon,
} from "lucide-react";

interface AuthLoginFormProps {
  onLogin: (username: string, token: string) => void;
}

export default function AuthLoginForm({ onLogin }: AuthLoginFormProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "登录失败，请检查用户名和密码");
      onLogin(username, data.token);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full pl-10 pr-12 py-3 rounded-xl border border-white/10 bg-white/[0.04] text-sm text-white placeholder-neutral-500 outline-none transition-all duration-200 focus:border-blue-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-blue-500/30";

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="mb-8 text-center">
        <div className="relative mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 via-blue-400 to-emerald-400 shadow-lg shadow-blue-500/30">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-emerald-400 opacity-40 blur-lg" />
          <Dna className="relative h-8 w-8 text-white" />
        </div>
        <h2 className="mb-1 text-2xl font-bold tracking-tight text-white">
          欢迎回来
        </h2>
        <p className="text-sm text-neutral-400">登录您的 BioAgent 账户</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-300">用户名</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <UserIcon className="h-5 w-5 text-neutral-500" />
            </div>
            <input
              className={inputBase}
              type="text"
              placeholder="请输入用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              autoComplete="username"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-300">密码</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <LockIcon className="h-5 w-5 text-neutral-500" />
            </div>
            <input
              className={inputBase}
              type={showPassword ? "text" : "password"}
              placeholder="请输入密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              aria-label={showPassword ? "隐藏密码" : "显示密码"}
            >
              {showPassword ? (
                <EyeOffIcon className="h-5 w-5 text-neutral-500 transition-colors hover:text-neutral-300" />
              ) : (
                <EyeIcon className="h-5 w-5 text-neutral-500 transition-colors hover:text-neutral-300" />
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <button
          className="group mt-2 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 px-4 py-3 font-medium text-white shadow-lg shadow-blue-500/20 transition-all duration-200 hover:from-blue-400 hover:to-emerald-400 hover:shadow-blue-500/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-neutral-900 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
              登录中...
            </>
          ) : (
            "登录"
          )}
        </button>
      </form>
    </div>
  );
}
